import React, { useState, useEffect } from "react";
import { Student } from "../../../services/Dtos";
import { VStack, HStack, Text, Box, Separator } from "@chakra-ui/react";
import {
  getClassOptions,
  getTodayDate,
  formatDate,
} from "../../../services/Utils";
import { genders, studentStatusOptions } from "../../../services/constants";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import CustomAlert from "../../CustomUi/CustomAlert";
import useIcons from "../../../hooks/useIcons";
import CustomModal from "../../CustomUi/CustomModal";
import TextInput from "../../CustomUi/TextInput";
import SelectInput from "../../CustomUi/SelectInput";
import { useFormChanges } from "../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../hooks/useFormFieldHandler";
import { useStudentService } from "../../../hooks/Domain/useStudentService";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import useUnsavedChangesPrompt from "../../../hooks/useUnsavedChangesPrompt";

// Type for student creation/update (without system fields)
type StudentFormData = Omit<
  Student,
  | "id"
  | "schoolId"
  | "createdAt"
  | "updatedAt"
  | "avatarUrl"
  | "publicId"
  | "guardians"
>;

interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  openGuardianForm: () => void;
  studentToEdit: StudentFormData | null;
  selectedStudentId: string;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({
  isOpen,
  onClose,
  studentToEdit,
  selectedStudentId,
}) => {
  const initialStudentFormState: StudentFormData = {
    firstName: "",
    middleName: "",
    lastName: "",
    gender: 0,
    currentClassId: "",
    dateOfBirth: getTodayDate(),
    dateOfRegistration: getTodayDate(),
    status: 0,
  };

  const { classes } = useSchoolsContext();
  const icons = useIcons();
  const [studentFormState, setStudentFormState] = useState<StudentFormData>(
    initialStudentFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize service hook
  const {
    createStudent,
    updateStudent,
    deleteStudent,
    errors,
    clearErrors,
    isLoading,
  } = useStudentService();

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    firstName: { type: "text" as const, required: true, maxLength: 50 },
    lastName: { type: "text" as const, required: true, maxLength: 50 },
    middleName: { type: "text" as const, required: false, maxLength: 50 },
    gender: { type: "select" as const, required: true },
    currentClassId: { type: "select" as const, required: true },
    status: { type: "select" as const, required: true },
    dateOfBirth: { type: "date" as const, required: true },
    dateOfRegistration: { type: "date" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: studentToEdit,
      currentData: studentFormState,
      isEditMode: !!studentToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setStudentFormState,
    setTouchedFields,
    integerFields: ["gender", "status"],
  });

  // Use unsaved changes prompt
  useUnsavedChangesPrompt(hasChanges);

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof StudentFormData;
    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (studentToEdit) {
      setStudentFormState(studentToEdit);
    } else {
      setStudentFormState(initialStudentFormState);
    }
    setTouchedFields(new Set());
  }, [studentToEdit, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      studentFormState[fieldName as keyof StudentFormData]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      studentFormState[fieldName as keyof StudentFormData]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (studentToEdit) {
      const response = await updateStudent(selectedStudentId, studentFormState);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createStudent(studentFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDeleteStudent = async () => {
    const response = await deleteStudent(selectedStudentId);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && studentToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const genderOptions = genders.map((g) => ({
    label: g.name,
    value: g.value.toString(),
  }));

  const classOptions = getClassOptions(classes);

  const statusOptions = studentStatusOptions;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDeleteStudent}
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={studentToEdit ? "Edit Student" : "Add New Student"}
        icon={icons.people.student}
        mode={studentToEdit ? "edit" : "add"}
        actionText={studentToEdit ? "Save Changes" : "Save Student"}
        openDeleteDialog={false}
        setOpenDeleteDialog={() => {}}
      >
        {/* Show only API/server errors */}
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Personal Information Section */}
          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={5} opacity={0.5}>
              Personal Information
            </Text>
            <VStack gap={5}>
              <HStack
                gap={4}
                w="full"
                align="flex-start"
                flexDirection={["column", "row"]}
                alignItems={["stretch", "center"]}
              >
                <TextInput
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={studentFormState.firstName}
                  onChange={handleFieldChange}
                  required
                  error={
                    showFieldError("firstName")
                      ? getFieldError("firstName")
                      : ""
                  }
                  invalid={showFieldError("firstName")}
                />

                <TextInput
                  label="Middle Name"
                  name="middleName"
                  type="text"
                  value={studentFormState.middleName}
                  onChange={handleFieldChange}
                  error={
                    showFieldError("middleName")
                      ? getFieldError("middleName")
                      : ""
                  }
                  invalid={showFieldError("middleName")}
                />
              </HStack>

              <TextInput
                label="Last Name"
                name="lastName"
                type="text"
                value={studentFormState.lastName}
                onChange={handleFieldChange}
                required
                error={
                  showFieldError("lastName") ? getFieldError("lastName") : ""
                }
                invalid={showFieldError("lastName")}
              />
            </VStack>
          </Box>

          <Separator />

          {/* Academic Information Section */}
          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={5} opacity={0.5}>
              Academic Information
            </Text>
            <VStack gap={4}>
              <HStack
                gap={4}
                w="full"
                flexDirection={["column", "row"]}
                alignItems={["stretch", "center"]}
              >
                <SelectInput
                  label="Gender"
                  name="gender"
                  onChange={handleFieldChange}
                  collection={genderOptions}
                  placeholder="Select gender"
                  required
                  error={
                    showFieldError("gender") ? getFieldError("gender") : ""
                  }
                  invalid={showFieldError("gender")}
                  value={studentFormState.gender.toString()}
                />

                <SelectInput
                  label="Class"
                  name="currentClassId"
                  onChange={(e) => handleFieldChange(e as any)}
                  collection={classOptions}
                  placeholder="Select class"
                  required
                  error={
                    showFieldError("currentClassId")
                      ? getFieldError("currentClassId")
                      : ""
                  }
                  invalid={showFieldError("currentClassId")}
                  value={studentFormState.currentClassId}
                />

                <SelectInput
                  label="Status"
                  name="status"
                  onChange={handleFieldChange}
                  collection={statusOptions}
                  placeholder="Select status"
                  error={
                    showFieldError("status") ? getFieldError("status") : ""
                  }
                  invalid={showFieldError("status")}
                  value={(studentFormState.status ?? 0).toString()}
                />
              </HStack>
            </VStack>
          </Box>

          <Separator />

          {/* Dates Section */}
          <Box>
            <Text fontSize="md" fontWeight="semibold" mb={5} opacity={0.5}>
              Important Dates
            </Text>
            <HStack
              gap={4}
              w="full"
              flexDirection={["column", "row"]}
              alignItems={["stretch", "center"]}
            >
              <TextInput
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formatDate(studentFormState.dateOfBirth)}
                onChange={handleFieldChange}
              />

              <TextInput
                label="Date of Registration"
                name="dateOfRegistration"
                type="date"
                value={formatDate(studentFormState.dateOfRegistration)}
                onChange={handleFieldChange}
              />
            </HStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default AddStudentModal;
